import React, {useEffect, useRef} from 'react'
import {useAuthSelector} from './authSlice';
import {useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Button, Message} from 'semantic-ui-react';
import {authActionCreators} from './authActionCreators';
import { firebaseService } from '../../services/firebaseService';
import { Spinner } from '../../components/spinner/Spinner';
import styles from './AuthInitializer.module.scss';

export const AuthInitializer: React.FunctionComponent = props => {
    const dispatch = useDispatch();
    // AuthInitializer sits outside every <Suspense> boundary, and translations are served by the
    // same API whose failure lands us in the error state, so never suspend here.
    const { t } = useTranslation(undefined, { useSuspense: false });

    const { initializationState, loading }  = useAuthSelector(state => state);
    const isInitialized = initializationState === "initialized";
    const showError = initializationState === "error" && !loading;
    const retryButton = useRef<Button>(null);

    useEffect(() => {
        const unsub = firebaseService.auth().onAuthStateChanged(async (user) => {
            dispatch(authActionCreators.initializeAuth());
        });
        return () => {
            unsub();
        };
    }, [dispatch]);

    // Retrying unmounts the panel, so hand focus back to the button whenever it reappears.
    useEffect(() => {
        if (showError) {
            retryButton.current?.focus();
        }
    }, [showError]);

    const retry = () => {
        dispatch(authActionCreators.initializeAuth());
    };

    return (
        <>
            <Spinner fullPage isLoading={loading} />
            {/* The app's message modal is mounted inside the children, so without this the page
                would render nothing at all when /auth fails. */}
            {showError && (
                <main className={styles.errorContainer}>
                    <Message negative role="alert">
                        <Message.Header>
                            {t('Auth.InitializationErrorHeader', 'We couldn\'t load the site')}
                        </Message.Header>
                        <p>
                            {t('Auth.InitializationErrorBody', 'We were unable to reach the server. Please check your connection and try again.')}
                        </p>
                    </Message>
                    <Button primary ref={retryButton} onClick={retry}>
                        {t('Auth.InitializationErrorRetry', 'Try again')}
                    </Button>
                </main>
            )}
            {isInitialized === true && (
                <>
                    {props.children}
                </>
            )}
        </>
    );
}
